import { Dayjs } from "dayjs";

// Define the IError interface
interface IError {
  error: {
    message: string;
    statusCode: number;
  };
  success: boolean;
  timestamp: Dayjs;
}

// Type guard to check if an error is an IError
const isIError = (error: unknown): error is IError => {
  return (
    typeof error === "object" &&
    error !== null &&
    "error" in error &&
    typeof (error as IError).error === "object" &&
    "message" in (error as IError).error &&
    typeof (error as IError).error.message === "string" &&
    "statusCode" in (error as IError).error &&
    typeof (error as IError).error.statusCode === "number" &&
    "success" in error &&
    typeof (error as IError).success === "boolean" &&
    "timestamp" in error
  );
};

// Extrait le message précis d'une réponse d'erreur Axios/NestJS brute, du type
const extractAxiosBackendMessage = (error: unknown): string | null => {
  const data = (error as any)?.response?.data;
  if (!data) return null;

  // Format enveloppé custom : { error: { message, statusCode } }
  if (isIError(data)) return data.error.message;

  // Format NestJS brut : { statusCode, message, error }
  if (typeof data.message === "string") return data.message;
  if (Array.isArray(data.message)) return data.message.join(", ");

  return null;
};

// Utility function to handle errors and return a formatted message
const handleError = (
  error: unknown,
  defaultMessage: string = "An error occurred"
): string => {
  if (isIError(error)) {
    return error.error.message || defaultMessage;
  }
  const backendMessage = extractAxiosBackendMessage(error);
  if (backendMessage) return backendMessage;

  if (error instanceof Error) {
    return error.message || defaultMessage;
  } else if (typeof error === "string") {
    return error;
  } else {
    return defaultMessage;
  }
};

// Utility function to throw an error with a formatted message
export const throwError = (
  error: unknown,
  defaultMessage: string = "An error occurred"
): never => {
  const message = handleError(error, defaultMessage);
  throw new Error(message);
};
